import {createPairPropertiesAssessor} from './raith-pair-properties.js';
import {createPackCartController} from './raith-pack-cart-controller.js';
import {validatePack, packCartConnectionLabels} from './raith-pack.js';

import {validatePair} from './raith-offers.js';

const mounts = new WeakMap();
const contextKeys = ['productId', 'sectionId', 'formId', 'marketId', 'country', 'language', 'currency', 'generation', 'amountDomain'];
const localeKeys = ['marketId', 'country', 'language', 'currency'];
const freeze = value => {if (value && typeof value === 'object') {Object.values(value).forEach(freeze); Object.freeze(value);} return value;};
const same = (a, b, keys) => a && b && keys.every(key => a[key] === b[key]);
const canonical = value => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object'
  ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value;
const equal = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
const contextKey = value => JSON.stringify(contextKeys.map(key => value?.[key]));
const one = (scope, selector) => {const nodes = scope.querySelectorAll(selector); return nodes.length === 1 ? nodes[0] : null;};
const rootPath = value => typeof value === 'string' && /^\/(?:[a-zA-Z0-9-]+\/?)*$/.test(value) ? value.replace(/\/?$/, '/') : null;
const label = value => typeof value === 'string' && value.trim() && value.length <= 2000 && !/translation missing/i.test(value);
const formAction = form => {try {const action = form.getAttribute('action'); return action ? new URL(action, form.ownerDocument.baseURI) : null;} catch {return null;}};
const imageMatches = (rendered, native) => rendered === native || rendered === `https://scdn.speedsize.com/9bb77832-4311-4122-b363-e277ea134e14/${native}`;
const permittedOrigin = value => {
  try {
    const url = new URL(value);
    // Theme Editor uses this store's permanent domain. Visitor-preview tokens
    // expire; retain this store's exact suffix for those shared previews.
    // The document and every native cart request must still use this same origin.
    return url.origin === value && url.protocol === 'https:' && !url.port
      && (url.hostname === 'raith-clo.com' || url.hostname === 'raithclothing.myshopify.com' || /^[a-z0-9]+-26611592\.shopifypreview\.com$/.test(url.hostname));
  } catch {return false;}
};

// Merchant activation uses this theme's rendered Shopify context.
// Cart QA still requires a separate, positively empty test session.
export async function mountPackCart(win, {assertPairProperties} = {}) {
  const doc = win?.document;
  if (!doc || mounts.has(doc)) return mounts.get(doc) || null;
  const query = new URL(win.location.href).searchParams;
  const stamp = one(doc, 'script[data-pack-cart-document]');
  let data;
  try {data = JSON.parse(stamp?.textContent);} catch {return null;}
  const packEnabled = params => data?.enabled === true || params.getAll('vl_pack_cart').length === 1 && params.get('vl_pack_cart') === '1';
  if (!packEnabled(query)) return null;
  const routesRoot = rootPath(data?.routesRoot);
  if (data?.version !== 1 || data.shop !== 'raithclothing.myshopify.com' || typeof data.themeId !== 'string' || !/^[1-9]\d*$/.test(data.themeId) || !['main', 'unpublished'].includes(data.role) || !permittedOrigin(data.origin) || !(data.component === 'raith-main-product' && typeof data.view === 'string' && /^[A-Za-z0-9_-]{0,255}$/.test(data.view)) || !routesRoot || !['171343983:GB:GBP', '96304333185:US:USD'].includes(`${data.marketId}:${data.country}:${data.currency}`) || data.language !== data.requestLocale || !data.language || !packCartConnectionLabels.concat('checkCart').every(key => label(data.labels?.[key]))) return null;
  const documentContext = freeze({...data, routesRoot});
  const stampText = stamp.textContent;
  const diagnosticEntries = [];
  const diagnostic = entry => {
    if (win.Shopify?.designMode !== true || !documentCurrent()) return;
    diagnosticEntries.push(entry);
    if (diagnosticEntries.length > 12) diagnosticEntries.shift();
    stamp.dataset.packCartDiagnostic = JSON.stringify(diagnosticEntries);
  };
  // Non-sensitive progress on the explicit draft trial only; never cart payloads.
  const stage = value => {if (stamp.dataset.packCartStage !== value) stamp.dataset.packCartStage = value;};
  const waiting = value => {stage(value); return null;};
  const knownContexts = new Set();
  let stopped = false, suspended = false, controller = null, binding = null, observer = null, queued = false, starting = false, retry = false;
  const listeners = new win.AbortController();
  const pairProperties = typeof assertPairProperties === 'function' ? null : createPairPropertiesAssessor({
    window: win, context: documentContext,
    current: () => binding && (binding.pairMode || binding.packet.source?.propertiesPolicy === 'standard-form-required') && sectionCurrent(binding.packet.context) ? binding : null,
    changed: refresh
  });
  const pairPolicy = assertPairProperties || (input => pairProperties.assert(input));

  function documentCurrent() {
    const s = win.Shopify, now = new URL(win.location.href);
    const themeQuery = now.searchParams.getAll('preview_theme_id');
    return !stopped && one(doc, 'script[data-pack-cart-document]') === stamp && stamp.isConnected && stamp.textContent === stampText
      && now.origin === documentContext.origin && now.pathname === documentContext.path
      && packEnabled(now.searchParams)
      && themeQuery.length <= 1 && (!themeQuery.length || themeQuery[0] === documentContext.themeId)
      && (!now.searchParams.has('view') || now.searchParams.getAll('view').length === 1 && now.searchParams.get('view') === documentContext.view)
      && s?.shop === documentContext.shop && String(s.theme?.id) === documentContext.themeId && s.theme?.role === documentContext.role
      && rootPath(s.routes?.root) === routesRoot && s.locale === documentContext.language && s.country === documentContext.country
      && s.currency?.active === documentContext.currency && doc.documentElement.lang === documentContext.requestLocale;
  }
  function findBinding() {
    if (!documentCurrent()) return waiting('document-context');
    const pairMode = doc.querySelectorAll('raith-pair-selector').length === 1 && doc.querySelectorAll('raith-pack-selector').length === 0;
    const selector = one(doc, pairMode ? 'raith-pair-selector' : 'raith-pack-selector'), script = selector && one(selector, pairMode ? 'script[data-offer-config]' : 'script[data-pack-config]');
    if (!pairMode && doc.querySelector('raith-pair-selector')) return waiting('ambiguous-selector');
    if (!selector?.current?.() || !selector.core?.model || !script) return waiting('selector');
    let model; try {model = pairMode ? validatePair(JSON.parse(script.textContent)) : validatePack(JSON.parse(script.textContent));} catch {return null;}
    if (!model || !equal(model, selector.core.model) || !same(model.context, documentContext, localeKeys) || (!pairMode && model.source.propertiesPolicy === 'none' && model.collectionId !== '481010188571')) return waiting('packet');
    const packet = pairMode ? {...model,context:{...model.context,amountDomain:'shopify-liquid-money-100'},products:model.source.items} : model;
    const c = packet.context, section = doc.getElementById(`shopify-section-${c.sectionId}`);
    if (!section?.contains(selector)) return waiting('section');
    const product = one(section, 'raith-product'), marker = product && one(product, '[data-offer-current]');
    // Native product forms contain input[name="id"], which shadows form.id.
    // Read markup attributes rather than the form's named-control properties.
    const forms = [...doc.forms].filter(form => form.getAttribute('id') === c.formId), form = forms.length === 1 ? forms[0] : null;
    if (!product || !marker || !form || !product.contains(form) || product.dataset.sectionId !== c.sectionId || product.dataset.formId !== c.formId
      || !same(marker.dataset, c, ['productId', 'sectionId', 'formId', ...localeKeys])) return waiting('product-form');
    const handle = marker.dataset.productHandle;
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle || '') || documentContext.path !== `${routesRoot}products/${handle}`) return waiting('product-path');
    const action = formAction(form);
    if (!action || form.getAttribute('method')?.toLowerCase() !== 'post' || action.origin !== documentContext.origin || action.pathname !== `${routesRoot}cart/add`) return waiting('form-route');
    return {selector, script, scriptText: script.textContent, core: selector.core, generation: pairMode ? selector.binding : selector.generation, packet, section, product, marker, form, pairMode};
  }
  function sectionCurrent(expectedContext) {
    const current = findBinding(), b = binding;
    return Boolean(b && current && same(expectedContext, b.packet.context, contextKeys)
      && ['selector', 'script', 'core', 'generation', 'section', 'product', 'marker', 'form', 'scriptText'].every(key => current[key] === b[key]));
  }
  function imagesCurrent({selection, expectedContext, products}) {
    if (!sectionCurrent(expectedContext) || !equal(products, binding.packet.products)) return false;
    const rows = binding.pairMode ? [...binding.selector.querySelectorAll('[data-offer-item]')] : [...binding.selector.querySelector('[data-pack-items]').children];
    return selection.rows.length === rows.length && selection.rows.every(row => {
      const product = binding.packet.products.find(item => (item.id || item.productId) === row.productId);
      const variant = product?.variants.find(item => item.id === row.variantId);
      const nodes = rows.filter(node => (binding.pairMode ? node.dataset.offerItem : node.dataset.packItem) === row.rowKey);
      const img = nodes.length === 1 && one(nodes[0], 'img');
      return product?.image && variant && equal(variant.optionValueIds, row.optionValueIds) && img && !img.hidden
        && imageMatches(img.getAttribute('src'), product.image.url) && Number(img.getAttribute('width')) === product.image.width && Number(img.getAttribute('height')) === product.image.height
        && img.complete && img.naturalWidth > 0;
    });
  }
  function refreshFeedback() {
    const button = binding && one(binding.selector, binding.pairMode ? '[data-offer-reconcile]' : '[data-pack-reconcile]');
    if (!button || !controller) return;
    const state = controller.state(), visible = state.uncertain && !stopped;
    if (button.hidden === visible) button.hidden = !visible;
    if (button.disabled !== state.busy) button.disabled = state.busy;
  }
  function stop() {
    stage('stopped');
    stopped = true; retry = false; observer?.disconnect(); listeners.abort();
    controller?.dispose(); pairProperties?.dispose(); refreshFeedback();
  }
  function refresh() {
    if (queued || stopped || suspended) return;
    queued = true;
    win.queueMicrotask(() => {
      queued = false;
      if (stopped || suspended) return;
      if (!documentCurrent()) {stop(); return;}
      if (!controller) {void start(); return;}
      const next = findBinding();
      if (!next) {controller.detach(); binding = null; pairProperties?.invalidate(); return;}
      if (!binding || !['selector', 'core', 'generation', 'script', 'scriptText', 'marker', 'form'].every(key => next[key] === binding[key])) {
        controller.detach(); binding = next; knownContexts.add(contextKey(next.packet.context));
        if (!controller.attach(next.selector, {expectedContext: next.packet.context, sections: [], sectionsUrl: win.location.pathname + win.location.search, labels: next.pairMode ? documentContext.pairLabels : {...documentContext.labels, add: next.packet.labels.add, incomplete: next.packet.labels.incomplete}})) binding = null;
      }
      pairProperties?.prepare(); controller.refresh(); refreshFeedback();
    });
  }
  async function start() {
    if (stopped || suspended || controller || starting || !documentCurrent()) return;
    const next = findBinding(), drawer = one(doc, '#cart-drawer');
    if (!next || typeof drawer?.presentCart !== 'function') {stage('native-drawer'); return;}
    starting = true;
    try {
      binding = next;
      controller = createPackCartController({window: win,
        origin: documentContext.origin, routesRoot, fetch: win.fetch.bind(win), timeoutMs: 5000, allowEditorQuery: () => win.Shopify?.designMode === true && documentCurrent(), diagnostic, assertPairProperties: pairPolicy, isPairPropertiesPending: () => pairProperties?.isPending() === true,
        assertContext: input => input.phase === 'document' ? documentCurrent() && equal(input.expectedContext, documentContext)
          : documentCurrent() && sectionCurrent(input.expectedContext),
        assertCartContext: input => documentCurrent() && input.origin === documentContext.origin && input.routesRoot === routesRoot
          && knownContexts.has(contextKey(input.expectedContext)) && same(input.expectedContext, documentContext, localeKeys),
        assertImageBindings: imagesCurrent});
      knownContexts.add(contextKey(next.packet.context));
      if (controller.start({expectedContext: documentContext}).status !== 'started') {stop(); return;}
      if (!controller.attach(next.selector, {expectedContext: next.packet.context, sections: [], sectionsUrl: win.location.pathname + win.location.search, labels: next.pairMode ? documentContext.pairLabels : {...documentContext.labels, add: next.packet.labels.add, incomplete: next.packet.labels.incomplete}})) binding = null;
      stage(binding ? 'connected' : 'attachment');
      pairProperties?.prepare(); refreshFeedback();
    } catch {stop();} finally {starting = false;}
  }
  const api = Object.freeze({refresh, dispose: stop, state: () => stopped ? 'stopped' : suspended ? 'suspended' : controller?.state().started ? 'started' : 'waiting'});
  mounts.set(doc, api);
  if (!documentCurrent()) {stop(); return api;}
  const signal = listeners.signal;
  const localization = node => node?.matches?.('form') && /\/localization\/?$/.test(formAction(node)?.pathname || '');
  doc.addEventListener('submit', event => {if (localization(event.target)) stop();}, {capture: true, signal});
  doc.addEventListener('change', event => {if (localization(event.target?.form) && ['country_code', 'language_code', 'locale_code'].includes(event.target.name)) stop();}, {capture: true, signal});
  win.addEventListener('pagehide', event => {
    if (!event.persisted) {stop(); return;}
    suspended = true; controller?.suspend();
  }, {signal});
  win.addEventListener('pageshow', event => {
    if (!event.persisted || stopped) return;
    suspended = false;
    if (!documentCurrent()) {stop(); return;}
    controller?.resume(); refresh();
  }, {signal});
  win.addEventListener('popstate', refresh, {signal});
  doc.addEventListener('input', refresh, {capture: true, signal});
  doc.addEventListener('change', refresh, {capture: true, signal});
  win.customElements.whenDefined('cart-drawer').then(refresh);
  doc.addEventListener('shopify:section:load', refresh, {signal});
  doc.addEventListener('load', refresh, {capture: true, signal});
  doc.addEventListener('error', refresh, {capture: true, signal});
  doc.addEventListener('click', event => {
    const action = event.target.closest?.('[data-pack-reconcile],[data-offer-reconcile]');
    if (action && binding?.selector.contains(action)) {
      event.preventDefault();
      if (!controller?.state().uncertain || controller.state().busy || !documentCurrent()) return;
      const work = controller.reconcile(); refreshFeedback();
      Promise.resolve(work).finally(() => {refreshFeedback(); refresh();});
    }
  }, {signal});
  observer = new win.MutationObserver(refresh);
  observer.observe(doc.documentElement, {subtree: true, childList: true, characterData: true, attributes: true,
    attributeFilter: ['id', 'class', 'src', 'lang', 'hidden', 'disabled', 'data-pack-state', 'name', 'form', 'value', 'required', 'type', 'data-pair-form-policy']});
  await start();
  return api;
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => {void mountPackCart(window);}, {once: true});
  else void mountPackCart(window);
}

